
import React from 'react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Calendar, Download } from "lucide-react";
import { useTranslation } from 'react-i18next';

interface SitemapUrl {
  loc: string;
  lastmod?: string;
  changefreq?: string;
  priority?: string;
}

interface SitemapUrlsProps {
  urls: SitemapUrl[];
  isLoading: boolean;
  formatDateTime: (dateString: string) => string;
  saveToFile: (content: string, filename: string) => void;
  domain: string;
}

export const SitemapUrls: React.FC<SitemapUrlsProps> = ({
  urls,
  isLoading,
  formatDateTime,
  saveToFile,
  domain
}) => {
  const { t } = useTranslation();

  return (
    <Card className="p-6">
      <CardHeader className="p-0 mb-4">
        <CardTitle>{t('analysis.sitemapUrls')}</CardTitle>
        <CardDescription>{t('analysis.allUrlsInSite')}</CardDescription>
      </CardHeader>
      <CardContent className="p-0 max-h-96 overflow-y-auto">
        {urls.length === 0 ? (
          <div className="text-center p-4">
            {isLoading ? t('analysis.loading') : t('analysis.noUrlFound')}
          </div>
        ) : (
          <div className="space-y-2">
            {urls.slice(0, 100).map((url, index) => (
              <div key={index} className="p-2 border-b last:border-0">
                <a href={url.loc} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline text-sm">
                  {url.loc}
                </a>
                {url.lastmod && (
                  <div className="text-xs text-muted-foreground mt-1 flex items-center">
                    <Calendar className="h-3 w-3 mr-1" />
                    {t('analysis.lastUpdated')} {formatDateTime(url.lastmod)}
                  </div>
                )}
              </div>
            ))}
            {urls.length > 100 && (
              <div className="p-2 text-center text-sm text-muted-foreground">
                {t('analysis.moreUrls', { count: urls.length - 100 })}
              </div>
            )}
          </div>
        )}
      </CardContent>
      <CardFooter className="pt-4 px-0 flex justify-between">
        <span className="text-sm text-muted-foreground">
          {t('analysis.totalFound', { count: urls.length })}
        </span>
        <Button 
          variant="outline" 
          size="sm"
          onClick={() => saveToFile(urls.map(url => url.loc).join('\n'), `${domain}-urls.txt`)}
        >
          <Download className="h-4 w-4 mr-1" />
          {t('analysis.downloadUrls')}
        </Button>
      </CardFooter>
    </Card>
  );
};
